import { isEmpty } from 'lodash';
import React, { FC, ReactNode } from 'react';

import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { IconBatteryCapacity } from 'src/general/Icons/BatteryCapacity';
import { IconElectricRange } from 'src/general/Icons/ElectricRange';
import { IconEnergyConsumption } from 'src/general/Icons/EnergyConsumption';
import { IconAncap } from 'src/general/Icons/IconAncap';
import { IconAutomaticTransmission } from 'src/general/Icons/IconAutomaticTransmission';
import { IconBodyType } from 'src/general/Icons/IconBodyType';
import { IconCalendar } from 'src/general/Icons/IconCalendar';
import { IconColour } from 'src/general/Icons/IconColour';
import { IconCylinders } from 'src/general/Icons/IconCylinders';
import { IconDriveType } from 'src/general/Icons/IconDriveType';
import { IconEmissions } from 'src/general/Icons/IconEmissions';
import { IconEngine } from 'src/general/Icons/IconEngine';
import { IconEnginePower } from 'src/general/Icons/IconEnginePower';
import { IconFuelConsumption } from 'src/general/Icons/IconFuelConsumption';
import { IconFuelTankCapacity } from 'src/general/Icons/IconFuelTankCapacity';
import { IconLength } from 'src/general/Icons/IconLength';
import { IconNumberOfKeys } from 'src/general/Icons/IconNumberOfKeys';
import { IconOdometer } from 'src/general/Icons/IconOdometer';
import { IconPetrol } from 'src/general/Icons/IconPetrol';
import { IconSeat } from 'src/general/Icons/IconSeat';
import { IconTowCapBraked } from 'src/general/Icons/IconTowCapBraked';
import { IconTransmission } from 'src/general/Icons/IconTransmission';
import { IconVIN } from 'src/general/Icons/IconVIN';
import { IconWeight } from 'src/general/Icons/IconWeight';
import { PdpDetailsBlock } from 'src/pdp/components/PdpDetailsBlock/PdpDetailsBlock';
import { getMicrocopy } from 'src/stores/microcopyStore';

import styles from './CarSummary.module.scss';

interface Props {
  bodyType: string;
  exteriorColour: string;
  numberOfKeys: string;
  vin: string;
  complianceYear: string;
  buildYear: string;
  odometer: string;
  transmission: string;

  fuelType: string;
  fuelTankCapacity: string;
  fuelConsumption: string;
  emissions: string;
  batteryCapacity: string;
  electricRange: string;
  energyConsumption: string;

  ancapSafetyRating: string;

  driveType: string;
  cylinders: string;
  gearbox: string;
  enginePower: string;
  engineSize: string;
  towCapBraked: string;

  seats: string;
  weight: string;
  length: string;

  isOnlyShowPdpDetails?: boolean;
}

interface SummaryItem {
  Icon: ReactNode;
  title: MICROCOPY;
  value: keyof Props;
  hideCondition?: (props: Props) => boolean;
}

export interface Footer {
  label: MICROCOPY;
  hideCondition: (props: Props) => boolean;
}

interface SummaryGroup {
  header: MICROCOPY;
  items: SummaryItem[];
  footer?: Footer;
}

const summaryContent: SummaryGroup[] = [
  {
    header: MICROCOPY.PDP_CAR_SUMMARY_SECTION_1_HEADER,
    items: [
      {
        Icon: <IconBodyType />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_1_TITLE_1,
        value: 'bodyType',
      },
      {
        Icon: <IconColour />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_1_TITLE_2,
        value: 'exteriorColour',
      },
      {
        Icon: <IconNumberOfKeys />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_1_TITLE_3,
        value: 'numberOfKeys',
      },
      {
        Icon: <IconVIN />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_1_TITLE_4,
        value: 'vin',
      },
      {
        Icon: <IconCalendar />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_1_TITLE_5,
        value: 'complianceYear',
      },
      {
        Icon: <IconCalendar />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_1_TITLE_6,
        value: 'buildYear',
      },
      {
        Icon: <IconOdometer />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_1_TITLE_7,
        value: 'odometer',
      },
      {
        Icon: <IconTransmission />,
        title: MICROCOPY.PDP_KEY_INFORMATION_TRANSMISSION,
        value: 'transmission',
      },
    ],
  },
  {
    header: MICROCOPY.PDP_CAR_SUMMARY_SECTION_2_HEADER,
    items: [
      {
        Icon: <IconPetrol />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_2_TITLE_1,
        value: 'fuelType',
      },
      {
        Icon: <IconElectricRange />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_2_TITLE_5,
        value: 'electricRange',
      },
      {
        Icon: <IconBatteryCapacity />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_2_TITLE_4,
        value: 'batteryCapacity',
      },
      {
        Icon: <IconEnergyConsumption />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_2_TITLE_6,
        value: 'energyConsumption',
      },
      {
        Icon: <IconFuelTankCapacity />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_2_TITLE_2,
        value: 'fuelTankCapacity',
        hideCondition: ({ fuelType }) => fuelType === 'Electric',
      },
      {
        Icon: <IconFuelConsumption />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_2_TITLE_3,
        value: 'fuelConsumption',
        hideCondition: ({ fuelType }) => fuelType === 'Electric',
      },
      {
        Icon: <IconEmissions />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_4_TITLE_1,
        value: 'emissions',
      },
    ],
    footer: {
      label: MICROCOPY.PDP_CAR_SUMMARY_SECTION_2_FOOTER,
      hideCondition: ({ energyConsumption, batteryCapacity, electricRange }) =>
        isEmpty(energyConsumption) && isEmpty(batteryCapacity) && isEmpty(electricRange),
    },
  },
  {
    header: MICROCOPY.PDP_CAR_SUMMARY_SECTION_3_HEADER,
    items: [
      {
        Icon: <IconAncap />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_3_TITLE_1,
        value: 'ancapSafetyRating',
      },
    ],
  },
  {
    header: MICROCOPY.PDP_CAR_SUMMARY_SECTION_4_HEADER,
    items: [
      {
        Icon: <IconDriveType />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_4_TITLE_2,
        value: 'driveType',
      },
      {
        Icon: <IconCylinders />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_4_TITLE_3,
        value: 'cylinders',
      },
      {
        Icon: <IconAutomaticTransmission />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_4_TITLE_4,
        value: 'gearbox',
      },
      {
        Icon: <IconEnginePower />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_4_TITLE_5,
        value: 'enginePower',
      },
      {
        Icon: <IconEngine />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_4_TITLE_6,
        value: 'engineSize',
      },
      {
        Icon: <IconTowCapBraked />,
        title: MICROCOPY.PDP_CAR_SUMMARY_TOW_CAP_BRAKED,
        value: 'towCapBraked',
      },
    ],
  },
  {
    header: MICROCOPY.PDP_CAR_SUMMARY_SECTION_5_HEADER,
    items: [
      {
        Icon: <IconSeat />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_5_TITLE_1,
        value: 'seats',
      },
      {
        Icon: <IconWeight />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_5_TITLE_2,
        value: 'weight',
      },
      {
        Icon: <IconLength />,
        title: MICROCOPY.PDP_CAR_SUMMARY_SECTION_5_TITLE_3,
        value: 'length',
      },
    ],
  },
];

export const CarSummary: FC<Props> = (props) => (
  <section>
    {!props?.isOnlyShowPdpDetails && (
      <h3 className={styles.header}>{getMicrocopy(MICROCOPY.PDP_CAR_SUMMARY_HEADER)}</h3>
    )}
    <div className={styles.list}>
      {summaryContent.map(({ header, items, footer }) => (
        <PdpDetailsBlock
          header={getMicrocopy(header)}
          footer={footer && !footer.hideCondition(props) && getMicrocopy(footer.label)}
          items={items
            .filter(({ value }) => !isEmpty(props[value]))
            .filter(({ hideCondition }) => !hideCondition || !hideCondition(props))
            .map(({ Icon, title, value }) => ({
              key: title,
              Icon,
              title: getMicrocopy(title),
              value: props[value],
            }))}
          key={header}
        />
      ))}
    </div>
  </section>
);
